import type { CrudEntitiesState, CrudType } from '@indutrust/shared-model';

/**
 * Return a partial state that sets the appropriate loading status for a CRUD action.
 *
 * @param crudType The CRUD operation to mark as loading. {@link CrudType}
 * @returns A partial {@link CrudEntitiesState} with the matching field set to 'loading'.
 * @example
 * const patch = setCrudLoading('add');
 */
export function setCrudLoading(crudType: CrudType): Partial<CrudEntitiesState> {
  switch (crudType) {
    case 'add':
      return { addStatus: 'loading' };
    case 'edit':
      return { editStatus: 'loading' };
    case 'delete':
      return { deleteStatus: 'loading' };
    case 'loadAll':
      return { loadAllStatus: 'loading' };
    case 'loadById':
      return { loadByIdStatus: 'loading' };
    default:
      return {};
  }
}

/**
 * Return a partial state that sets the appropriate success status for a CRUD action.
 *
 * @param crudType The CRUD operation to mark as success. {@link CrudType}
 * @returns A partial {@link CrudEntitiesState} with the matching field set to 'success'.
 * @example
 * const patch = setCrudSuccess('edit');
 */
export function setCrudSuccess(crudType: CrudType): Partial<CrudEntitiesState> {
  switch (crudType) {
    case 'add':
      return { addStatus: 'success' };
    case 'edit':
      return { editStatus: 'success' };
    case 'delete':
      return { deleteStatus: 'success' };
    case 'loadAll':
      return { loadAllStatus: 'success' };
    case 'loadById':
      return { loadByIdStatus: 'success' };
    default:
      return {};
  }
}

/**
 * Return a partial state that sets an error for a CRUD action.
 *
 * @param crudType The CRUD operation that failed. {@link CrudType}
 * @param error The error message to store.
 * @returns A partial {@link CrudEntitiesState} with the matching field set to `{ error }`.
 * @example
 * const patch = setCrudError('delete', 'Network failed');
 */
export function setCrudError(
  crudType: CrudType,
  error: string
): Partial<CrudEntitiesState> {
  switch (crudType) {
    case 'add':
      return { addStatus: { error } };
    case 'edit':
      return { editStatus: { error } };
    case 'delete':
      return { deleteStatus: { error } };
    case 'loadAll':
      return { loadAllStatus: { error } };
    case 'loadById':
      return { loadByIdStatus: { error } };
    default:
      return {};
  }
}

/**
 * Return a partial state that resets a CRUD action back to 'initial'.
 *
 * @param crudType The CRUD operation to reset. {@link CrudType}
 * @returns A partial {@link CrudEntitiesState} with the matching field set to 'initial'.
 * @example
 * const patch = setCrudInitial('loadAll');
 */
export function setCrudInitial(crudType: CrudType): Partial<CrudEntitiesState> {
  switch (crudType) {
    case 'add':
      return { addStatus: 'initial' };
    case 'edit':
      return { editStatus: 'initial' };
    case 'delete':
      return { deleteStatus: 'initial' };
    case 'loadAll':
      return { loadAllStatus: 'initial' };
    case 'loadById':
      return { loadByIdStatus: 'initial' };
    default:
      return {};
  }
}
